import { useLayoutEffect, useRef, useState } from 'react';
import { ArenaChoice } from './arena-choice.js';
import './arena-guide.css';

export interface ArenaGuideProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly guildId: string;
}

function guideStorageKey(guildId: string): string {
  return `guildrise:arena-guide:v1:${guildId}`;
}

/** Call when entering the arena; the parent owns the open state. */
export function shouldShowGuide(guildId: string): boolean {
  if (!guildId) return false;
  try {
    return localStorage.getItem(guideStorageKey(guildId)) !== 'seen';
  } catch {
    return true;
  }
}

export function markGuideSeen(guildId: string): void {
  if (!guildId) return;
  try {
    localStorage.setItem(guideStorageKey(guildId), 'seen');
  } catch {
    // The guide remains usable when browser storage is unavailable.
  }
}

/** Owns one ArenaChoice; the parent handles Telegram Back and repeat opening. */
export function ArenaGuide({ open, onClose, guildId }: ArenaGuideProps) {
  const [artFailed, setArtFailed] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (open && content.current) content.current.scrollTop = 0;
  }, [open]);
  function close() {
    markGuideSeen(guildId);
    onClose();
  }

  return (
    <ArenaChoice open={open} title="Добро пожаловать" presentation="page" onClose={close}>
      <div className="arena-guide" ref={content}>
        <div className="arena-guide__intro">
          {!artFailed && (
            <img
              src="/art/hero-420.webp"
              width={420}
              height={579}
              alt="Улыбающаяся проводница с волшебной книгой"
              decoding="async"
              onError={() => setArtFailed(true)}
            />
          )}
          <div>
            <p className="arena-guide__welcome">Давай соберём твою первую колоду.</p>
            <p>
              Привет! Впервые здесь? Я помогу разобраться. Пять коротких шагов — и можно играть.
            </p>
          </div>
        </div>
        <ol className="arena-guide__steps">
          <li>
            <h3>Выбери 9 разных персонажей</h3>
            <p>
              Добавляй карты из своей коллекции. Вселенные можно сочетать как угодно. В начале в
              руке 5 карт, перед своим ходом добираешь одну.
            </p>
          </li>
          <li>
            <h3>Одна карта — один голос</h3>
            <p>
              Редкость — только оформление. Карты голосуют за свою зону. Выиграй больше зон, чем
              соперник; при равенстве — ничья. Если на двоих меньше трёх вселенных, полный раунд
              закончится ничьей.
            </p>
          </li>
          <li>
            <h3>Четыре действия в любом порядке</h3>
            <p>За раунд используй каждое один раз. Начинать с Секрета необязательно.</p>
            <dl className="arena-guide__actions">
              <div>
                <dt>
                  Секрет <span>1 карта</span>
                </dt>
                <dd>Спрячь свою карту до конца раунда.</dd>
              </div>
              <div>
                <dt>
                  Сброс <span>1 карта</span>
                </dt>
                <dd>Убери карту из раунда: она не голосует.</dd>
              </div>
              <div>
                <dt>
                  Подарок <span>3 карты</span>
                </dt>
                <dd>Соперник забирает одну, две остаются тебе.</dd>
              </div>
              <div>
                <dt>
                  Разделение <span>4 карты</span>
                </dt>
                <dd>Составь две пары. Соперник выбирает одну, другая твоя.</dd>
              </div>
            </dl>
          </li>
          <li>
            <h3>Ответ сопернику — не твой ход</h3>
            <p>
              Выбирая карту из Подарка или пару из Разделения, ты не тратишь своё действие и не
              добираешь карту. Владение коллекцией не меняется. Старые матчи завершаются по прежним
              весам: значения больше одного видны на картах.
            </p>
          </li>
          <li>
            <h3>На решение есть 30 секунд</h3>
            <p>
              Если время истечёт, игра выберет допустимый вариант автоматически. Выход не
              останавливает таймер. Подсказку можно снова открыть кнопкой «?».
            </p>
          </li>
        </ol>
      </div>
      <footer className="arena-guide__footer">
        <button type="button" className="btn btn--primary" onClick={close}>
          Понятно, к колоде
        </button>
      </footer>
    </ArenaChoice>
  );
}
